import React, { useMemo } from 'react';
import { useProjectStore } from '../store/useProjectStore';
import { getCardDimensions } from './Card/cardUtils';
import { useTheme } from '../context/ThemeContext';
import { useSimulationSafe } from '../context/SimulationContext';

// ============================================================================
// LeadTimeLadder V20.2 — Escada de Lead Time VSM
// Renderiza DOIS artefatos:
//   1. LeadTimeLadderSvg — <g> SVG em coordenadas canvas nativas
//      Inserido via prop canvasSvgChildren do VsmCanvas → sync perfeito com zoom/pan
//   2. LeadTimeLadderOverlay — caixa PLT/VAT position:fixed fora do canvas
// ============================================================================
//
// Padrão Lean correto:
//   Degrau BAIXO (VA):  processo → produto sendo transformado (linha desce)
//   Degrau ALTO (NVA):  estoque/espaço vazio → dinheiro parado (linha sobe/mantém)
//
// Linha contínua: espaços entre cards = NVA (linha permanece em cima)
// ============================================================================

const FONT    = `9px 'JetBrains Mono','Courier New',monospace`;
const VA_DOWN = 24; // V147.0: amplitude suficiente para duas células de texto
const LADDER_BOTTOM_PADDING = 20;
const LABEL_LINE_OFFSET = 8; // V142.0: offset generoso anti-colisão

interface FlowStep {
  tipo: 'processo' | 'inventory' | 'fifo' | 'gap' | 'cell';
  id: string;
  x: number;
  width: number;
  label: string;
  flowCellId?: string;
  valueSeconds?: number;
  nvaLift?: number;
  severity?: 'normal' | 'error';
}

export function useFlowData() {
  const mapType       = useProjectStore(s => s.mapType);
  const globalDemand  = useProjectStore(s => s.globalDemand);
  const factoryWorkDays = useProjectStore(s => s.factoryWorkDays);
  const sheets        = useProjectStore(s => s.sheets);
  const activeSheetId = useProjectStore(s => s.activeSheetId);
  const viewMode = useProjectStore(s => s.viewMode);
  const simulation = useSimulationSafe();

  const toSeconds = (value: unknown, unit: unknown, fallbackUnit: 's' | 'min' | 'h' = 's') => {
    const numericValue = Number(value) || 0;
    const resolvedUnit = typeof unit === 'string' && unit.length > 0 ? unit : fallbackUnit;

    switch (resolvedUnit) {
      case 'dias':
        return numericValue * 86400;
      case 'horas':
      case 'h':
        return numericValue * 3600;
      case 'minutos':
      case 'min':
        return numericValue * 60;
      case 'segundos':
      case 's':
      default:
        return numericValue;
    }
  };

  return useMemo(() => {
    if (mapType !== 'FACTORY' || !activeSheetId) {
      return { steps: [], totalVAT_s: 0, totalPLT_days: 0, totalBaselinePLT_days: 0 };
    }
    const sheet = sheets.find(s => s.id === activeSheetId);
    const allCards = (sheet?.cards || []).filter(c => c.status !== 'deletado');
    // V203: no modo futuro, ocultar cards marcados como vsmFuturoHide (eliminados do mapa futuro)
    const isFuturoView = viewMode === 'future_simulated';
    const cards = isFuturoView
      ? allCards.filter(c => !(c as any).vsmFuturoHide)
      : allCards;
    // V203: future_simulated consolida células igual ao kaizen_work
    const isOptimizationView = viewMode === 'kaizen_work' || isFuturoView;
    const getFlowCellId = (card: any): string | undefined => isOptimizationView ? card?.vsmFlowCellId : undefined;
    const isInternalFlowCellStock = (card: any): boolean => {
      const cellId = getFlowCellId(card);
      if (!cellId || !card?.vsmFlowCellBlocked) return false;

      const cellProcesses = cards.filter(
        candidate => getFlowCellId(candidate) === cellId && candidate.tipo === 'atividade' && (candidate as any).vsmIcon === 'processo',
      );
      if (cellProcesses.length < 2) return false;

      const processCenters = cellProcesses.map(candidate => {
        const dims = getCardDimensions(candidate, false, false);
        return {
          x: candidate.posicao.x + dims.width / 2,
          y: candidate.posicao.y + dims.height / 2,
        };
      });
      const spreadX = Math.max(...processCenters.map(center => center.x)) - Math.min(...processCenters.map(center => center.x));
      const spreadY = Math.max(...processCenters.map(center => center.y)) - Math.min(...processCenters.map(center => center.y));
      const primaryAxis = spreadX >= spreadY ? 'x' : 'y';
      const minCenter = Math.min(...processCenters.map(center => center[primaryAxis]));
      const maxCenter = Math.max(...processCenters.map(center => center[primaryAxis]));
      const dims = getCardDimensions(card, false, false);
      const cardCenter = primaryAxis === 'x'
        ? card.posicao.x + dims.width / 2
        : card.posicao.y + dims.height / 2;

      return cardCenter > minCenter && cardCenter < maxCenter;
    };
    const getFlowCellLeadTimeReduction = (flowCellId?: string): number => {
      if (!flowCellId) return 0;
      const value = cards
        .filter(card => getFlowCellId(card) === flowCellId)
        .reduce((max, card) => Math.max(max, Number((card as any).vsmFlowCellLeadTimeReductionPercent) || 0), 0);
      return Math.max(0, Math.min(100, value));
    };
    const getFlowCellProjectedCycleSeconds = (flowCellId?: string): number => {
      if (!flowCellId) return 0;
      return cards
        .filter(card => getFlowCellId(card) === flowCellId)
        .reduce((max, card) => Math.max(max, Number((card as any).vsmFlowCellProjectedCycleSeconds) || 0), 0);
    };
    const getKanbanLoopLeadTimeDays = (inventoryCardId: string): number => {
      const values = cards.flatMap(card => (card.conexoes_saida ?? [])
        .filter(conn => conn.connectionType === 'vsm_info' && conn.kanbanLoopEnabled && conn.kanbanSupermarketId === inventoryCardId)
        .map(conn => Number(conn.kanbanLoopLeadTimeDays))
        .filter(value => Number.isFinite(value) && value > 0));
      return values.length > 0 ? Math.max(...values) : 0;
    };

    // V206: cell members devem permanecer em processNodes para contribuir ao VAT total.
    // A consolidação em tipo:'cell' já ocorre no final via cellSummaries.
    // A exclusão prematura (V203) zeraba o VAT da célula — corrigido.
    const processNodes = cards
      .filter(c => c.tipo === 'atividade' && (c as any).vsmIcon === 'processo')
      .sort((a, b) => a.posicao.x - b.posicao.x);

    const fifoInventoryIds = new Set<string>();
    cards.forEach(source => {
      (source.conexoes_saida ?? []).forEach(conn => {
        if (conn.connectionType !== 'vsm_fifo') return;
        const target = cards.find(card => card.id === conn.targetId);
        if (source.tipo === 'inventory') fifoInventoryIds.add(source.id);
        if (target?.tipo === 'inventory') fifoInventoryIds.add(target.id);
      });
    });

    // V208.1: in future mode, exclude estoque cards adjacent to flow cell members (same logic as VsmCanvas isAdjacentToCell)
    const cellMemberIdsForPLT = isFuturoView
      ? new Set(cards.filter(c => (c as any).vsmFlowCellId && !(c as any).vsmFlowCellBlocked).map(c => c.id))
      : new Set<string>();
    const isAdjacentToCellInFuture = (c: any): boolean => {
      if (!isFuturoView || c.tipo !== 'inventory' || (c as any).vsmIcon !== 'estoque') return false;
      if ((c as any).vsmConvertedToIcon) return false;
      if ((c.conexoes_saida ?? []).some((rc: any) => cellMemberIdsForPLT.has(rc.targetId))) return true;
      return cards.some(src => cellMemberIdsForPLT.has(src.id) &&
        (src.conexoes_saida ?? []).some((rc: any) => rc.targetId === c.id));
    };
    const inventoryNodes = cards.filter(c => c.tipo === 'inventory' && !fifoInventoryIds.has(c.id) && !isAdjacentToCellInFuture(c));
    const fifoConnectionSteps = cards.flatMap(source =>
      (source.conexoes_saida ?? [])
        .filter(conn => conn.connectionType === 'vsm_fifo')
        .map(conn => ({ source, conn, target: cards.find(card => card.id === conn.targetId) }))
        .filter(item => item.target && item.source.tipo === 'atividade' && item.target.tipo === 'atividade')
    );

    const flowItems = [
      ...processNodes.map(card => ({ kind: 'card' as const, card, x: card.posicao.x })),
      ...inventoryNodes.map(card => ({ kind: 'card' as const, card, x: card.posicao.x })),
      ...fifoConnectionSteps.map(({ source, conn, target }) => {
        const sourceDims = getCardDimensions(source, false, false);
        const targetDims = getCardDimensions(target!, false, false);
        const hasSourceDataBox = !!((source as any).vsmCT || (source as any).vsmCO || (source as any).vsmDisp);
        const hasTargetDataBox = !!((target as any).vsmCT || (target as any).vsmCO || (target as any).vsmDisp);
        const sourceWidth = hasSourceDataBox ? 121 : sourceDims.width;
        const targetWidth = hasTargetDataBox ? 121 : targetDims.width;
        const sourceX = hasSourceDataBox ? source.posicao.x + (sourceDims.width - 121) / 2 : source.posicao.x;
        const targetX = hasTargetDataBox ? target!.posicao.x + (targetDims.width - 121) / 2 : target!.posicao.x;
        const laneStart = sourceX + sourceWidth;
        const laneEnd = targetX;
        return {
          kind: 'fifo' as const,
          id: conn.id,
          source,
          target: target!,
          conn,
          x: Math.min(laneStart, laneEnd),
          width: Math.max(24, Math.abs(laneEnd - laneStart)),
        };
      }),
    ].sort((a, b) => a.x - b.x);

    if (flowItems.length === 0) {
      return { steps: [], totalVAT_s: 0, totalPLT_days: 0, totalBaselinePLT_days: 0 };
    }

    const getDailyDemand = (card: any): number => {
      const explicitDailyDemand = Number(card?.vsmDemanda) || 0;
      if (explicitDailyDemand > 0) return explicitDailyDemand;

      const monthlyMix = Array.isArray(card?.vsmDemandMix) ? card.vsmDemandMix : [];
      const monthlyTotal = monthlyMix.reduce((sum: number, item: any) => sum + (Number(item?.qty) || 0), 0);
      const workDays = Number(card?.vsmDiasMes) || Number(factoryWorkDays) || 20;
      return monthlyTotal > 0 && workDays > 0 ? monthlyTotal / workDays : 0;
    };

    const demandCandidates = cards
      .filter(c => c.tipo === 'card_final' || ['cliente', 'externo'].includes((c as any).vsmIcon ?? ''))
      .map(card => ({ card, demand: getDailyDemand(card) }))
      .filter(item => item.demand > 0)
      .sort((a, b) => {
        const priority = (card: any) => card.tipo === 'card_final' || card.vsmIcon === 'cliente' ? 0 : 1;
        return priority(a.card) - priority(b.card);
      });
    const effectiveDemand = demandCandidates[0]?.demand || globalDemand;
    const simIsActive = Boolean(simulation?.isActive ?? simulation?.isSimulationMode);
    const shiftMinutes = simulation?.shiftPreset === '2_shifts' ? 960 : simulation?.shiftPreset === 'plus_2h' ? 600 : 480;
    const simDemand = simIsActive ? Number(simulation?.demand ?? simulation?.simulatedDemand ?? 0) || effectiveDemand : effectiveDemand;

    const getSimulatedInventory = (inventoryNode: any) => {
      const inventory = Number(inventoryNode.inventory) || 0;
      if (!simIsActive || simDemand <= 0) return inventory;

      const nextProcess = cards
        .filter(card => card.tipo === 'atividade' && (card as any).vsmIcon === 'processo' && card.posicao.x >= inventoryNode.posicao.x)
        .sort((a, b) => a.posicao.x - b.posicao.x)[0] as any;
      if (!nextProcess) return inventory;

      const override = simulation?.kaizenOverrides?.[nextProcess.id];
      const cycleSeconds = (() => {
        const vsmCTRaw = Number(nextProcess.vsmCT) || 0;
        if (vsmCTRaw > 0) return toSeconds(vsmCTRaw, nextProcess.vsmCTUnit, 's');
        return toSeconds(nextProcess.tp, nextProcess.tpUnit, 'min');
      })();
      const cycleMinutes = cycleSeconds / 60;
      const operators = Math.max(1, Number(nextProcess.vsmOp ?? nextProcess.pessoasSimultaneas ?? 1));
      const extraOperators = Math.max(0, Number(override?.fte ?? override?.extraFTE ?? 0));
      const ctReduction = Math.max(0, Math.min(100, Number(override?.rework ?? override?.reworkReduction ?? 0)));
      const adjustedCycle = cycleMinutes * (1 - ctReduction / 100);
      const capacityPerDay = adjustedCycle > 0 ? (shiftMinutes * (operators + extraOperators)) / adjustedCycle : 0;
      const dailyAccumulation = capacityPerDay > 0 ? Math.max(0, simDemand - capacityPerDay) : 0;
      return inventory + dailyAccumulation;
    };

    const steps: FlowStep[] = [];
    let totalVAT_s    = 0;
    let totalPLT_days = 0;
    let totalBaselinePLT_days = 0;

    for (let i = 0; i < flowItems.length; i++) {
      const item = flowItems[i];
      const node = item.kind === 'card' ? item.card : null;
      const dims = node ? getCardDimensions(node, false, false) : { width: item.width, height: 0 };
      // processo+databox renders at the same width as processo/entity cards.
      const hasDataBox = !!node && node.tipo === 'atividade' && (node as any).vsmIcon === 'processo'
        && !!((node as any).vsmCT || (node as any).vsmCO || (node as any).vsmDisp);
      const pdWidth = 121;
      let nodeW = hasDataBox ? pdWidth : dims.width;
      let nodeX = item.kind === 'fifo'
        ? item.x
        : hasDataBox && node
        ? node.posicao.x + (dims.width - pdWidth) / 2
        : node!.posicao.x;
      if (node && (node as any).vsmIcon === 'supermercado' && (node as any).vsmConvertedFromIcon === 'estoque') {
        nodeW = Math.round(nodeW * 1.5);
        nodeX = node.posicao.x + Math.round(dims.width * 0.76);
      }

      // Gap entre card anterior e este
      if (i > 0) {
        const prev = flowItems[i - 1];
        const prevNode = prev.kind === 'card' ? prev.card : null;
        const prevHasDataBox = !!prevNode && prevNode.tipo === 'atividade' && (prevNode as any).vsmIcon === 'processo'
          && !!((prevNode as any).vsmCT || (prevNode as any).vsmCO || (prevNode as any).vsmDisp);
        const prevDims = prevNode ? getCardDimensions(prevNode, false, false) : { width: prev.width, height: 0 };
        const prevVisualX = prev.kind === 'fifo'
          ? prev.x
          : prevHasDataBox && prevNode
          ? prevNode.posicao.x + (prevDims.width - pdWidth) / 2
          : prevNode!.posicao.x;
        const prevW = prev.kind === 'fifo' ? prev.width : prevHasDataBox ? pdWidth : prevDims.width;
        const gapStart = prevVisualX + prevW;
        const gapEnd   = nodeX;
        if (gapEnd - gapStart > 4) {
          const flowCellId = !!node && !!prevNode && getFlowCellId(node) === getFlowCellId(prevNode)
            ? getFlowCellId(node)
            : undefined;
          steps.push({ tipo: flowCellId ? 'cell' : 'gap', id: `gap-${i}`, x: gapStart, width: gapEnd - gapStart, label: '', flowCellId });
        }
      }

      if (item.kind === 'fifo') {
        const maxCapacity = Math.max(1, Number(item.conn.fifoMaxCapacity ?? 50) || 50);
        const currentWip = Math.max(0, Number(item.conn.fifoCurrentWip ?? 0) || 0);
        const fifoDays = simDemand > 0 ? maxCapacity / simDemand : 0;
        const saturated = currentWip > maxCapacity;
        const labelDays = fifoDays % 1 === 0 ? fifoDays.toFixed(0) : fifoDays.toFixed(1);
        const label = saturated
          ? `SAT ${Math.round(currentWip)}/${Math.round(maxCapacity)}`
          : `FIFO ${labelDays}d`;
        steps.push({
          tipo: 'fifo',
          id: item.id,
          x: nodeX,
          width: nodeW,
          label,
          severity: saturated ? 'error' : 'normal',
          nvaLift: Math.min(18, Math.max(0, fifoDays * 1.4)),
        });
        totalPLT_days += fifoDays;
        totalBaselinePLT_days += fifoDays;
        continue;
      }

      if (!node) continue;

      if (node.tipo === 'inventory') {
        if (isOptimizationView && isInternalFlowCellStock(node)) {
          steps.push({ tipo: 'cell', id: node.id, x: nodeX, width: nodeW, label: '', flowCellId: getFlowCellId(node) });
          continue;
        }

        const override = simulation?.kaizenOverrides?.[node.id];
        const leadTimeGain = Math.max(0, Math.min(100, Number(override?.targetMetric === 'leadTime' ? override?.gainPercent : 0)));
        const flowCellId = getFlowCellId(node);
        const cellLeadTimeGain = getFlowCellLeadTimeReduction(flowCellId);
        const combinedLeadTimeGain = Math.min(100, leadTimeGain + cellLeadTimeGain);
        if (['supermercado', 'pulmao'].includes((node as any).vsmIcon ?? '') || ['supermercado', 'pulmao'].includes((node as any).vsmConvertedToIcon ?? '')) {
          const kanbanLoopLeadTimeDays = getKanbanLoopLeadTimeDays(node.id);
          const fixedLeadTimeDays = Number(kanbanLoopLeadTimeDays || (node as any).vsmFixedLeadTimeDays);
          const hasFixedLeadTime = Number.isFinite(fixedLeadTimeDays) && fixedLeadTimeDays > 0;
          const fixedBufferDays = Math.max(0, hasFixedLeadTime ? fixedLeadTimeDays : Number((node as any).vsmBufferDays ?? (node as any).vsmDias ?? 1));
          const adjustedBufferDays = hasFixedLeadTime ? fixedBufferDays : fixedBufferDays * (1 - combinedLeadTimeGain / 100);
          const labelValue = adjustedBufferDays.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
          const label = hasFixedLeadTime ? `${labelValue} d` : `${labelValue} dia`;
          steps.push({ tipo: 'inventory', id: node.id, x: nodeX, width: nodeW, label, flowCellId, nvaLift: Math.min(18, Math.max(0, adjustedBufferDays * 1.4)) });
          totalPLT_days += adjustedBufferDays;
          totalBaselinePLT_days += fixedBufferDays;
          continue;
        }
        const explicitDays = Number((node as any).vsmDias);
        const inventory     = getSimulatedInventory(node);
        const hasExplicitDays = Number.isFinite(explicitDays) && explicitDays > 0;
        const baselineInventoryDays = hasExplicitDays
          ? explicitDays
          : simDemand > 0 ? inventory / simDemand : 0;
        const inventoryDays = baselineInventoryDays * (1 - combinedLeadTimeGain / 100);
        const daysLabel = inventoryDays % 1 === 0 ? inventoryDays.toFixed(0) : inventoryDays.toFixed(1);
        const label = inventoryDays > 0
          ? `${daysLabel} dias`
          : (inventory > 0 ? `${inventory}pç` : '');
        steps.push({ tipo: 'inventory', id: node.id, x: nodeX, width: nodeW, label, flowCellId, nvaLift: Math.min(18, Math.max(0, inventoryDays * 1.4)) });
        totalPLT_days += inventoryDays;
        totalBaselinePLT_days += baselineInventoryDays;
      } else {
        const baselineProcessSeconds = (() => {
          const vsmCTRaw = Number((node as any).vsmCT) || 0;
          if (vsmCTRaw > 0) {
            return toSeconds(vsmCTRaw, (node as any).vsmCTUnit, 's');
          }

          return toSeconds((node as any).tp, (node as any).tpUnit, 'min');
        })();
        const override = simulation?.kaizenOverrides?.[node.id];
        const cycleGain = Math.max(0, Math.min(100, Number(override?.targetMetric !== 'leadTime' ? override?.gainPercent ?? override?.rework ?? override?.reworkReduction ?? 0 : 0)));
        // Projeção Kaizen → só é aplicada no Estado Futuro. Overrides de simulação
        // (`timeOverrideSeconds`/`timeOverride`) continuam respeitados em qualquer view,
        // pois representam intervenção explícita do usuário no modo simulação.
        const projectedSeconds = Number(
          (override as any)?.timeOverrideSeconds
          ?? (override as any)?.timeOverride
          ?? (isFuturoView ? (node as any).vsmKaizenProjection?.metrics?.cycleTime?.projectedSeconds : undefined)
          ?? (isFuturoView ? (node as any).vsmKaizenProjection?.projectedSeconds : undefined)
          ?? 0,
        ) || 0;
        const processSeconds = projectedSeconds > 0 ? projectedSeconds : baselineProcessSeconds * (1 - cycleGain / 100);
        const label = processSeconds > 0
          ? (processSeconds >= 120 ? `${(processSeconds / 60).toFixed(1)}m` : `${processSeconds.toFixed(0)}s`)
          : '';
        steps.push({ tipo: 'processo', id: node.id, x: nodeX, width: nodeW, label, flowCellId: getFlowCellId(node), valueSeconds: processSeconds });
        totalVAT_s    += processSeconds;
        totalPLT_days += processSeconds / 86400;
        totalBaselinePLT_days += baselineProcessSeconds / 86400;
      }
    }

    const cellSummaries = new Map<string, { x: number; ex: number; totalSeconds: number; minSeconds: number; processCount: number }>();
    steps.forEach(step => {
      if (!step.flowCellId) return;
      const summary = cellSummaries.get(step.flowCellId) ?? { x: step.x, ex: step.x + step.width, totalSeconds: 0, minSeconds: Number.POSITIVE_INFINITY, processCount: 0 };
      summary.x = Math.min(summary.x, step.x);
      summary.ex = Math.max(summary.ex, step.x + step.width);
      if (step.tipo === 'processo') {
        const stepSeconds = Number(step.valueSeconds) || 0;
        summary.totalSeconds += stepSeconds;
        if (stepSeconds > 0) summary.minSeconds = Math.min(summary.minSeconds, stepSeconds);
        summary.processCount += 1;
      }
      cellSummaries.set(step.flowCellId, summary);
    });

    cellSummaries.forEach((summary, flowCellId) => {
      if (summary.processCount < 2) return;
      const projectedCycleSeconds = getFlowCellProjectedCycleSeconds(flowCellId);
      const cellCycleSeconds = projectedCycleSeconds > 0
        ? projectedCycleSeconds
        : Number.isFinite(summary.minSeconds) ? summary.minSeconds : summary.totalSeconds;
      const cycleDeltaSeconds = cellCycleSeconds - summary.totalSeconds;
      totalVAT_s += cycleDeltaSeconds;
      totalPLT_days += cycleDeltaSeconds / 86400;
    });

    const consolidatedSteps: FlowStep[] = [];
    const emittedCellSummaries = new Set<string>();
    steps.forEach(step => {
      const summary = step.flowCellId ? cellSummaries.get(step.flowCellId) : undefined;
      if (!step.flowCellId || !summary || summary.processCount < 2) {
        consolidatedSteps.push(step);
        return;
      }

      if (!emittedCellSummaries.has(step.flowCellId)) {
        const projectedCycleSeconds = getFlowCellProjectedCycleSeconds(step.flowCellId);
        const cellCycleSeconds = projectedCycleSeconds > 0
          ? projectedCycleSeconds
          : Number.isFinite(summary.minSeconds) ? summary.minSeconds : 0;
        consolidatedSteps.push({
          tipo: 'cell',
          id: `cell-summary-${step.flowCellId}`,
          x: summary.x,
          width: summary.ex - summary.x,
          label: cellCycleSeconds > 0 ? formatVAT(cellCycleSeconds) : '',
          flowCellId: step.flowCellId,
          valueSeconds: cellCycleSeconds,
        });
        emittedCellSummaries.add(step.flowCellId);
      }
    });

    // V139.0 — Injetar Info Lead Time das caixas de informação quando simulação ativa
    if (simIsActive) {
      const infoBoxCards = cards.filter(
        (c) => c.tipo === 'atividade' && (c as any).vsmIcon === 'comunicacao' && Number((c as any).vsmInfoLeadTime) > 0,
      );
      for (const infoCard of infoBoxCards) {
        totalPLT_days += Number((infoCard as any).vsmInfoLeadTime);
        totalBaselinePLT_days += Number((infoCard as any).vsmInfoLeadTime);
      }
    }

    return { steps: consolidatedSteps, totalVAT_s, totalPLT_days, totalBaselinePLT_days };
  }, [mapType, globalDemand, factoryWorkDays, sheets, activeSheetId, viewMode, simulation?.isActive, simulation?.isSimulationMode, simulation?.demand, simulation?.simulatedDemand, simulation?.shiftPreset, simulation?.kaizenOverrides]);
}

function useBaseY() {
  const sheets        = useProjectStore(s => s.sheets);
  const activeSheetId = useProjectStore(s => s.activeSheetId);
  return useMemo(() => {
    const sheet = sheets.find(s => s.id === activeSheetId);
    const cards = (sheet?.cards || []).filter(c => c.status !== 'deletado');
    if (cards.length === 0) return 400;
    const maxBottom = Math.max(...cards.map(c => {
      const dims = getCardDimensions(c, false, false);
      const hasDataBox = c.tipo === 'atividade' && (c as any).vsmIcon === 'processo'
        && !!((c as any).vsmCT || (c as any).vsmCO || (c as any).vsmDisp);
      const h = hasDataBox ? Math.round(90 / (104 / 207)) : dims.height;
      return c.posicao.y + h;
    }));
    return maxBottom + 100 + LADDER_BOTTOM_PADDING;
  }, [sheets, activeSheetId]);
}

// ─── SVG em coordenadas canvas nativas — injetado via canvasSvgChildren ──────
function formatPLT(days: number): string {
  if (days < 1) return `${(days * 24).toFixed(1)}h`;
  return `${days % 1 === 0 ? days.toFixed(0) : days.toFixed(1)}d`;
}
function formatVAT(s: number): string {
  if (s >= 3600) return `${(s / 3600).toFixed(1)}h`;
  if (s >= 120)  return `${(s / 60).toFixed(1)}m`;
  return `${s.toFixed(0)}s`;
}

export function LeadTimeLadderSvg() {
  const mapType = useProjectStore(s => s.mapType);
  const { steps, totalVAT_s, totalPLT_days, totalBaselinePLT_days } = useFlowData();
  const baseY = useBaseY();

  if (mapType !== 'FACTORY' || steps.length === 0) return null;

  const { isDark } = useTheme();
  const pathColor     = isDark ? '#cbd5e1' : '#111827';
  const vaTextFill    = isDark ? '#e2e8f0' : '#111';
  const nvaTextFill   = isDark ? '#94a3b8' : '#555';
  const axisLabelFill = isDark ? '#64748b' : '#9ca3af';
  const boxBg         = isDark ? 'rgba(15,23,42,0.92)' : 'rgba(248,250,252,0.96)';
  const hasSaturation = steps.some(step => step.severity === 'error');
  const pltColor      = hasSaturation ? '#dc2626' : isDark ? '#7dd3fc' : '#0369a1';
  const vatColor      = isDark ? '#86efac' : '#15803d';

  const nvaY = baseY;
  const vaY  = baseY + VA_DOWN;

  // V147.0: Box fused with staircase — left = right edge of last step (no gap)
  const pts:   string[] = [];
  const texts: React.ReactNode[] = [];
  let curY: number | null = null;
  let lastEx = steps[0]?.x ?? 0;

  steps.forEach((step) => {
    const sx = Math.round(step.x);
    const ex = Math.round(step.x + step.width);
    const cx = Math.round(sx + (ex - sx) / 2);
    const targetY = step.tipo === 'processo' || step.tipo === 'cell' ? vaY : nvaY;

    if (curY === null) {
      pts.push(`M ${sx} ${Math.round(targetY)}`);
    } else if (curY !== targetY) {
      pts.push(`V ${Math.round(targetY)}`);
    }
    pts.push(`H ${ex}`);
    curY = targetY;
    lastEx = ex;

    if ((step.tipo === 'processo' || step.tipo === 'cell') && step.label) {
      texts.push(
        <text key={`va-${step.id}`} x={cx} y={vaY - LABEL_LINE_OFFSET}
          textAnchor="middle" dominantBaseline="auto"
          style={{ font: FONT, fill: vaTextFill, fontWeight: 'bold' }}>
          {step.label}
        </text>
      );
    } else if ((step.tipo === 'inventory' || step.tipo === 'fifo') && step.label) {
      texts.push(
        <text key={`nva-${step.id}`} x={cx} y={nvaY - LABEL_LINE_OFFSET}
          textAnchor="middle"
          style={{ font: FONT, fill: step.severity === 'error' ? '#dc2626' : nvaTextFill, fontWeight: step.severity === 'error' ? 'bold' : 400 }}>
          {step.label}
        </text>
      );
    }
  });

  // V147.0: Box geometry — top=nvaY, bottom=vaY, height=VA_DOWN (= stair amplitude)
  const boxX   = Math.round(lastEx);
  const boxW   = 76;
  const boxMid = nvaY + VA_DOWN / 2;
  const hasLeadTimeGain = totalBaselinePLT_days > totalPLT_days + 0.01;

  // Path extension: left wall (V nvaY) + top (H) + right wall (V vaY)
  if (curY !== null) {
    if (curY !== nvaY) pts.push(`V ${nvaY}`);
    pts.push(`H ${boxX + boxW}`);
    pts.push(`V ${vaY}`);
  }

  return (
    <g style={{ pointerEvents: 'none' }} shapeRendering="crispEdges">
      <text x={steps[0].x - 4} y={nvaY} textAnchor="end" dominantBaseline="middle"
        style={{ font: FONT, fill: axisLabelFill }}>NVA</text>
      <text x={steps[0].x - 4} y={vaY} textAnchor="end" dominantBaseline="middle"
        style={{ font: FONT, fill: axisLabelFill }}>VA</text>
      {/* Box fill — rendered before path */}
      <rect x={boxX} y={nvaY} width={boxW} height={VA_DOWN} fill={boxBg} />
      {/* Staircase + box left wall + top + right wall (single continuous path) */}
      {pts.length > 0 && (
        <path d={pts.join(' ')} stroke={pathColor} strokeWidth={1.5} fill="none"
          strokeLinejoin="miter" strokeLinecap="square" />
      )}
      {/* Box bottom — VA line extension */}
      <line x1={boxX} y1={vaY} x2={boxX + boxW} y2={vaY} stroke={pathColor} strokeWidth={1.5} />
      {/* Box midline divider */}
      <line x1={boxX} y1={boxMid} x2={boxX + boxW} y2={boxMid} stroke={pathColor} strokeWidth={1} opacity={0.6} />
      {texts}
      {/* PLT: baseline aligned with NVA step labels (nvaY - LABEL_LINE_OFFSET) */}
      <text x={boxX + 8} y={nvaY - LABEL_LINE_OFFSET} dominantBaseline="auto"
        style={{ font: FONT, fill: pltColor, fontWeight: 'bold' }}>
        {hasSaturation ? `PLT: SAT ${formatPLT(totalPLT_days)}` : hasLeadTimeGain ? `PLT: ${formatPLT(totalBaselinePLT_days)}>${formatPLT(totalPLT_days)}` : `PLT: ${formatPLT(totalPLT_days)}`}
      </text>
      {/* VAT: baseline aligned with VA step labels (vaY - LABEL_LINE_OFFSET) */}
      <text x={boxX + 8} y={vaY - LABEL_LINE_OFFSET} dominantBaseline="auto"
        style={{ font: FONT, fill: vatColor, fontWeight: 'bold' }}>
        {`VAT: ${formatVAT(totalVAT_s)}`}
      </text>
    </g>
  );
}

// ─── Footer unificado: escada VA/NVA + KPIs — posição fixa, não segue pan ────
export function LeadTimeLadderFooter({
  pan,
  scale,
  contentScale,
  sidebarWidth = 0,
}: {
  pan: { x: number; y: number };
  scale: number;
  contentScale: number;
  /** Width of the left sidebar in px (needed to align the SVG with the canvas) */
  sidebarWidth?: number;
}) {
  const { isDark } = useTheme();
  const mapType = useProjectStore(s => s.mapType);
  const { steps, totalVAT_s, totalPLT_days, totalBaselinePLT_days } = useFlowData();

  if (mapType !== 'FACTORY' || steps.length === 0) return null;

  const sf = scale * (contentScale / 12);
  // V155.0: Geometria compacta — amplitude −30% (GAP 17px vs 24px)
  const H     = 43;
  const nvaY  = 20;
  const vaY   = 37;          // nvaY + GAP(17)
  const GAP   = vaY - nvaY; // 17
  const FSIZE = `8px 'JetBrains Mono',monospace`;

  const pathColor   = isDark ? '#cbd5e1' : '#111827';
  const gridColor   = isDark ? '#1e293b' : '#e2e8f0';
  const textColor   = isDark ? '#dbeafe' : '#1f2937';
  const vaTextColor = isDark ? '#cbd5e1' : '#374151';
  const bg          = isDark ? 'rgba(10,17,35,0.82)' : 'rgba(248,250,252,0.88)';
  const bdr         = isDark ? 'rgba(30,41,59,0.6)'  : 'rgba(226,232,240,0.6)';
  const boxBg       = isDark ? 'rgba(15,23,42,0.96)' : 'rgba(241,245,249,0.98)';
  const hasSaturation = steps.some(step => step.severity === 'error');
  const pltColor    = hasSaturation ? '#dc2626' : isDark ? '#7dd3fc' : '#0369a1';
  const vatColor    = isDark ? '#86efac' : '#15803d';
  const hasLeadTimeGain = totalBaselinePLT_days > totalPLT_days + 0.01;

  // V155.0: Filtrar passos — terminar no último processo (box não deriva por inventários trailing)
  const lastProcIdx = steps.reduce((max, s, i) => (s.tipo === 'processo' || s.tipo === 'cell') ? i : max, -1);
  const visibleSteps = lastProcIdx >= 0 ? steps.slice(0, lastProcIdx + 1) : steps;

  const pts:   string[] = [];
  const texts: React.ReactNode[] = [];
  let curY: number | null = null;
  let lastEx = sidebarWidth;
  let lastProcessScreenCenterX = sidebarWidth;
  let lastProcessScreenLeft  = sidebarWidth;
  let lastProcessScreenRight = sidebarWidth + 80;

  visibleSteps.forEach((step) => {
    const fx      = Math.round(step.x * sf + pan.x + sidebarWidth);
    const fw      = Math.round(step.width * sf);
    const ex      = fx + fw;
    const cx      = Math.round(fx + fw / 2);
    const targetY = step.tipo === 'processo' || step.tipo === 'cell' ? vaY : nvaY;

    if (curY === null) {
      pts.push(`M ${fx} ${targetY}`);
    } else if (curY !== targetY) {
      pts.push(`V ${targetY}`);
    }
    pts.push(`H ${ex}`);
    curY = targetY;
    lastEx = ex;
    if (step.tipo === 'processo' || step.tipo === 'cell') {
      lastProcessScreenCenterX = cx;
      lastProcessScreenLeft  = fx;
      lastProcessScreenRight = ex;
    }

    // V157.0: VA (ciclo) ACIMA da linha vaY — 8px offset, nunca abaixo
    if ((step.tipo === 'processo' || step.tipo === 'cell') && step.label) {
      texts.push(
        <text key={`va-${step.id}`} x={cx} y={vaY - 8} textAnchor="middle"
          dominantBaseline="auto"
          style={{ font: FSIZE, fill: vaTextColor, fontWeight: 700 }}>
          {step.label}
        </text>
      );
    } else if ((step.tipo === 'inventory' || step.tipo === 'fifo') && step.label) {
      texts.push(
        <text key={`nva-${step.id}`} x={cx} y={nvaY - 8} textAnchor="middle"
          dominantBaseline="auto"
          style={{ font: FSIZE, fill: step.severity === 'error' ? '#dc2626' : textColor, fontWeight: 800 }}>
          {step.label}
        </text>
      );
    }
  });

  // V157.2: Box alinhado com bordas do último processo (paredes coincidem com a escada)
  const boxX   = lastProcessScreenLeft;
  const boxW   = lastProcessScreenRight - lastProcessScreenLeft;
  const boxMid = nvaY;
  const axisGutterWidth = sidebarWidth > 0 ? sidebarWidth : 40;
  const axisLabelX = Math.round(axisGutterWidth / 2);
  const axisGutterBg = isDark ? '#0f172a' : '#f8fafc';

  // V157.1: Escada termina na linha nvaY — sem paredes laterais no box
  if (curY !== null) {
    if (curY !== nvaY) pts.push(`V ${nvaY}`);
  }

  return (
    <div style={{
      position: 'absolute', bottom: 0, left: 0, width: '100%', height: H,
      background: bg, borderTop: `1px solid ${bdr}`,
      zIndex: 50, pointerEvents: 'none', overflow: 'hidden',
      fontFamily: "'JetBrains Mono', monospace",
    }}>
      <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible' }}
        shapeRendering="crispEdges">
        {/* Linhas guia horizontais — full-width */}
        <line x1={0} y1={nvaY} x2="100%" y2={nvaY} stroke={gridColor} strokeWidth={0.5} />
        <line x1={0} y1={vaY}  x2="100%" y2={vaY}  stroke={gridColor} strokeWidth={0.5} />
        {/* Box fill — cobre ambas as células PLT e VAT */}
        <rect x={boxX} y={nvaY - GAP} width={boxW} height={GAP * 2} fill={boxBg} />
        {/* Escada — path termina na linha nvaY, sem paredes laterais */}
        {pts.length > 0 && (
          <path d={pts.join(' ')} stroke={pathColor} strokeWidth={1.5} fill="none"
            strokeLinejoin="miter" strokeLinecap="square" />
        )}
        {/* Box: paredes laterais (boxX e boxX+boxW) cobrindo PLT+VAT */}
        <line x1={boxX}        y1={nvaY - GAP} x2={boxX}        y2={vaY} stroke={pathColor} strokeWidth={1.5} />
        <line x1={boxX + boxW} y1={nvaY - GAP} x2={boxX + boxW} y2={vaY} stroke={pathColor} strokeWidth={1.5} />
        {/* Linha topo (acima de PLT) — simétrica à vaY abaixo de VAT */}
        <line x1={boxX} y1={nvaY - GAP} x2={boxX + boxW} y2={nvaY - GAP} stroke={pathColor} strokeWidth={1.5} />
        {/* Linha separadora PLT/VAT — continua a gridline nvaY sobre o box */}
        <line x1={boxX} y1={nvaY} x2={boxX + boxW} y2={nvaY} stroke={pathColor} strokeWidth={1.5} />
        {texts}
        {/* PLT: alinhado com os labels NVA (nvaY - 8, dominantBaseline auto) */}
        <text x={boxX + boxW / 2} y={nvaY - 8}
          textAnchor="middle" dominantBaseline="auto"
          style={{ font: FSIZE, fill: pltColor, fontWeight: 700 }}>
          {hasSaturation ? `PLT:SAT ${formatPLT(totalPLT_days)}` : hasLeadTimeGain ? `PLT:${formatPLT(totalBaselinePLT_days)}>${formatPLT(totalPLT_days)}` : `PLT:${formatPLT(totalPLT_days)}`}
        </text>
        {/* VAT: 8px acima da linha vaY (respiro obrigatório), auto para não tocar a linha */}
        <text x={boxX + boxW / 2} y={vaY - 8}
          textAnchor="middle" dominantBaseline="auto"
          style={{ font: FSIZE, fill: vatColor, fontWeight: 700 }}>
          {`VAT:${formatVAT(totalVAT_s)}`}
        </text>
        {/* Gutter fixo opaco: bloqueia geometria móvel sob NVA/VA na área da sidebar */}
        <rect x={0} y={0} width={axisGutterWidth} height={H} fill={axisGutterBg} />
        <line x1={axisGutterWidth} y1={0} x2={axisGutterWidth} y2={H} stroke={bdr} strokeWidth={1} />
        {/* Labels de eixo: fixos no extremo esquerdo do footer, acima da máscara */}
        <text x={axisLabelX} y={nvaY - 8} textAnchor="middle" dominantBaseline="auto"
          style={{ font: FSIZE, fill: textColor, fontWeight: 800 }}>NVA</text>
        <text x={axisLabelX} y={vaY - 8} textAnchor="middle" dominantBaseline="auto"
          style={{ font: FSIZE, fill: textColor, fontWeight: 800 }}>VA</text>
      </svg>
    </div>
  );
}

// ─── Overlay PLT/VAT legado — mantido por compat, use LeadTimeLadderFooter ───
export function LeadTimeLadderOverlay() {
  return null;
}

// ─── Compat default export (mantém imports existentes sem erros) ──────────────
export default function LeadTimeLadder() {
  return null;
}

